/**
 * StealthOS Configuration
 *
 * Base/EVM only.
 * Customer would set these values for their integrations.
 */
import { ethers } from "ethers";
import { MUCHFI_CHIKYU } from "./muchfi";
import {
  DEVELOPMENT_EVM_CHAIN_IDS,
  DOGEOS_CHIKYU_CHAIN_ID,
  EVM_NETWORK_DEFINITIONS,
  evmNetworkKeyForPath,
  evmWalletChainParams,
  readDogeOsChikyuConfig,
  type DogeOsChikyuConfig,
  type EvmDerivationDescriptor,
  type EvmNetworkKey,
  type EvmWalletChainParams,
} from "./evmNetworks";

/**
 * Chain type
 */
export type ChainType = "evm";
export type ProvingProfile =
  | "attested_tee_proving"
  | "server_proving"
  | "browser_proving";
type VerificationMode = "zk_required" | "tee_only";
type BaseFungibleTokenHashMode = "pool_v3" | "legacy_poseidon";
type BaseWithdrawBindingVersion = "v1" | "v2";
export type BaseSettlementStack = "nova" | "flat_joinsplit_v2";
const BASE_PROTOCOL_FEE_BPS = Number(
  process.env.REACT_APP_PROTOCOL_FEE_BPS ?? 15,
);
const BASE_PARTNER_FEE_BPS = Number(
  process.env.REACT_APP_PARTNER_FEE_BPS ?? 15,
);
const BASE_SWAP_PROTOCOL_FEE_BPS = Number(
  process.env.REACT_APP_SWAP_PROTOCOL_FEE_BPS ?? BASE_PROTOCOL_FEE_BPS,
);
const BASE_SWAP_PARTNER_FEE_BPS = Number(
  process.env.REACT_APP_SWAP_PARTNER_FEE_BPS ?? BASE_PARTNER_FEE_BPS,
);

function normalizeVerificationMode(
  value: string | null | undefined,
  envName: string,
): VerificationMode {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "zk_required" || normalized === "tee_only") {
    return normalized;
  }
  throw new Error(`${envName} must be zk_required or tee_only`);
}

function normalizeBaseFungibleTokenHashMode(
  value: string | null | undefined,
): BaseFungibleTokenHashMode {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "pool_v3" || normalized === "legacy_poseidon") {
    return normalized;
  }
  throw new Error(
    "REACT_APP_BASE_FUNGIBLE_TOKEN_HASH_MODE must be pool_v3 or legacy_poseidon",
  );
}

function resolveBaseFungibleTokenHashMode(
  verificationMode: VerificationMode,
): BaseFungibleTokenHashMode {
  const explicitMode = process.env.REACT_APP_BASE_FUNGIBLE_TOKEN_HASH_MODE;
  if (explicitMode && explicitMode.trim().length > 0) {
    return normalizeBaseFungibleTokenHashMode(explicitMode);
  }
  return verificationMode === "tee_only" ? "legacy_poseidon" : "pool_v3";
}

function normalizeBaseWithdrawBindingVersion(
  value: string | null | undefined,
): BaseWithdrawBindingVersion {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "v1" || normalized === "v2") {
    return normalized;
  }
  throw new Error("REACT_APP_BASE_WITHDRAW_BINDING_VERSION must be v1 or v2");
}

function resolveBaseWithdrawBindingVersion(
  verificationMode: VerificationMode,
): BaseWithdrawBindingVersion {
  const explicitVersion = process.env.REACT_APP_BASE_WITHDRAW_BINDING_VERSION;
  if (explicitVersion && explicitVersion.trim().length > 0) {
    return normalizeBaseWithdrawBindingVersion(explicitVersion);
  }
  return verificationMode === "tee_only" ? "v1" : "v2";
}

function normalizeProvingProfile(value?: string | null): ProvingProfile {
  const normalized = String(value || "").toLowerCase();
  if (
    normalized === "attested_tee_proving" ||
    normalized === "server_proving" ||
    normalized === "browser_proving"
  ) {
    return normalized;
  }
  throw new Error(
    "REACT_APP_PROVING_PROFILE must be attested_tee_proving, server_proving, or browser_proving",
  );
}

function resolveProvingProfile(): ProvingProfile {
  return normalizeProvingProfile(
    process.env.REACT_APP_PROVING_PROFILE ||
      process.env.PROVING_PROFILE ||
      "attested_tee_proving",
  );
}

function positiveIntegerEnv(name: string, fallback: number): number {
  const raw = process.env[name];
  if (!raw || raw.trim().length === 0) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
}

/**
 * Base configuration (shared)
 */
const DEFAULT_BASE_PROVER_URL = "https://base-prover.stealthos.xyz";
const ENABLE_WALLET_SCREENING =
  (
    process.env.REACT_APP_ENABLE_WALLET_SCREENING ||
    process.env.REACT_APP_ENABLE_RANGE_SCREENING ||
    "true"
  ).toLowerCase() !== "false";
const WALLET_SCREENING_PROVIDER = (
  process.env.REACT_APP_WALLET_SCREENING_PROVIDER ||
  process.env.REACT_APP_SCREENING_PROVIDER ||
  ((process.env.REACT_APP_ENABLE_CHAINALYSIS_SCREENING || "").toLowerCase() ===
  "true"
    ? "chainalysis"
    : "range")
).toLowerCase();
const BASE_CHAIN_ID = Number(process.env.REACT_APP_CHAIN_ID || 8453);
export const PREVIOUS_POOL_MODE =
  BASE_CHAIN_ID === 84532 &&
  !!process.env.REACT_APP_PRIVATE_FEE_POOL &&
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("pool") === "previous";
const PREVIOUS_POOL = "0x0AC5544c51e6E37E0294b95539F0F74607Bef3F8";
const IS_PRODUCTION_BUILD = process.env.NODE_ENV === "production";
/** Only explicitly supported test/local chains may use development artifacts. */
export function isProductionChainId(chainId: number): boolean {
  return !DEVELOPMENT_EVM_CHAIN_IDS.has(Number(chainId));
}

/** The chain the Base deployment (`REACT_APP_CHAIN_ID`) serves at `/base`. */
export const BASE_DEPLOYMENT_CHAIN_ID = BASE_CHAIN_ID;

/**
 * DogeOS Chikyu: a second EVM network with its own prefixed configuration,
 * served at `/dogeos` only when its pool is configured (phase 1c of
 * DOGEOS_MYDOGE_JOINSPLIT_PLAN.md). The record is read once here; the
 * registry, the router and the app read it from this module.
 */
export const DOGEOS_CHIKYU: DogeOsChikyuConfig = readDogeOsChikyuConfig(
  process.env,
);

/**
 * The EVM network this page serves, decided from the mount path at module
 * load (as PREVIOUS_POOL_MODE already does with the query string): every
 * module-scope consumer of EVM_CONFIG then sees one network for the life of
 * the page, and the Base configuration is never touched by DogeOS values.
 */
export const ACTIVE_EVM_NETWORK_KEY: EvmNetworkKey =
  typeof window === "undefined"
    ? "base"
    : evmNetworkKeyForPath(window.location.pathname);
const DOGEOS_ACTIVE = ACTIVE_EVM_NETWORK_KEY === "dogeos-chikyu";

/** What the app needs to know about the network it is serving. */
export interface EvmNetworkDescriptor {
  key: string;
  chainId: number;
  label: string;
  /** Mount path; payment links live at `${route}/send`. */
  route: string;
  production: boolean;
  nativeSymbol: string;
  explorerUrl: string;
  walletChainParams: EvmWalletChainParams;
  derivation: EvmDerivationDescriptor;
  /** What this network signed before `derivation` (walletCarryOver.ts). */
  legacyDerivation: EvmDerivationDescriptor;
  /** Served: false for a registry network without its pool or misconfigured. */
  enabled: boolean;
  configurationError?: string;
  /**
   * Set for prefixed registry networks (DogeOS Chikyu): the key the browser
   * sends to /api/auth/nonce and /api/relayer-status. Undefined for the
   * legacy Base deployment, whose requests stay byte-identical.
   */
  registryKey?: string;
}

function baseNetworkDescriptor(): EvmNetworkDescriptor {
  const definition = EVM_NETWORK_DEFINITIONS[BASE_CHAIN_ID];
  const fallback = EVM_NETWORK_DEFINITIONS[84532];
  return {
    key: definition?.key ?? `evm-${BASE_CHAIN_ID}`,
    chainId: BASE_CHAIN_ID,
    label: definition?.label ?? `EVM chain ${BASE_CHAIN_ID}`,
    route: "/base",
    production: isProductionChainId(BASE_CHAIN_ID),
    nativeSymbol: (definition ?? fallback).nativeCurrency.symbol,
    explorerUrl: (definition ?? fallback).explorerUrl,
    walletChainParams: evmWalletChainParams(BASE_CHAIN_ID),
    derivation: (definition ?? fallback).derivation,
    legacyDerivation: (definition ?? fallback).legacyDerivation,
    enabled: true,
  };
}

function dogeOsNetworkDescriptor(
  network: DogeOsChikyuConfig,
): EvmNetworkDescriptor {
  const definition = EVM_NETWORK_DEFINITIONS[DOGEOS_CHIKYU_CHAIN_ID];
  return {
    key: network.key,
    chainId: network.chainId,
    label: network.label,
    route: network.route,
    production: false,
    nativeSymbol: definition.nativeCurrency.symbol,
    explorerUrl: network.explorerUrl,
    walletChainParams: evmWalletChainParams(network.chainId, {
      explorerUrl: network.explorerUrl,
      publicRpcUrls: network.publicRpcUrls,
    }),
    derivation: definition.derivation,
    legacyDerivation: definition.legacyDerivation,
    enabled: network.enabled,
    configurationError: network.configurationError,
    registryKey: network.key,
  };
}

function resolveBaseSettlementStack(): BaseSettlementStack {
  const value = String(
    process.env.REACT_APP_BASE_SETTLEMENT_STACK || "nova",
  ).toLowerCase();
  if (value === "nova" || value === "flat_joinsplit_v2") return value;
  throw new Error(
    "REACT_APP_BASE_SETTLEMENT_STACK must be nova or flat_joinsplit_v2",
  );
}

function nonNegativeIntegerEnv(name: string, fallback = 0): number {
  const raw = process.env[name];
  if (!raw || raw.trim().length === 0) return fallback;
  const parsed = Number(raw);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    throw new Error(`${name} must be a non-negative safe integer`);
  }
  return parsed;
}

function exactSha256Env(name: string): string {
  const value = String(process.env[name] || "").toLowerCase();
  if (value && !/^[0-9a-f]{64}$/.test(value)) {
    throw new Error(`${name} must be an exact 64-character SHA-256 hex pin`);
  }
  return value;
}

const BASE_VERIFICATION_MODE = normalizeVerificationMode(
  process.env.REACT_APP_BASE_VERIFICATION_MODE || "zk_required",
  "REACT_APP_BASE_VERIFICATION_MODE",
);
const BASE_FUNGIBLE_TOKEN_HASH_MODE = resolveBaseFungibleTokenHashMode(
  BASE_VERIFICATION_MODE,
);
const BASE_WITHDRAW_BINDING_VERSION = resolveBaseWithdrawBindingVersion(
  BASE_VERIFICATION_MODE,
);
const PROVING_PROFILE = resolveProvingProfile();
const BASE_SETTLEMENT_STACK = resolveBaseSettlementStack();
const FLAT_JOINSPLIT_V2_ENABLED = BASE_SETTLEMENT_STACK === "flat_joinsplit_v2";
const FLAT_JOINSPLIT_V2_DEPLOYMENT_BLOCK = nonNegativeIntegerEnv(
  "REACT_APP_JOINSPLIT_V2_DEPLOYMENT_BLOCK",
);
const FLAT_JOINSPLIT_V2_ALLOW_DEVELOPMENT_ARTIFACTS =
  String(
    process.env.REACT_APP_JOINSPLIT_V2_ALLOW_DEVELOPMENT_ARTIFACTS || "false",
  ).toLowerCase() === "true";
const FLAT_JOINSPLIT_V2_SEPOLIA_OPTIMIZED_DEV_ARTIFACTS =
  process.env.REACT_APP_JOINSPLIT_V2_SEPOLIA_OPTIMIZED_DEV_ARTIFACTS === "true";
if (
  FLAT_JOINSPLIT_V2_SEPOLIA_OPTIMIZED_DEV_ARTIFACTS &&
  BASE_CHAIN_ID !== 84532
) {
  throw new Error(
    "Optimized development-artifact opt-in is restricted to Base Sepolia (84532)",
  );
}

// NODE_ENV alone is the wrong gate: a development build pointed at a live chain
// would happily load development proving keys against the production verifier.
// The target chain decides; an optimized testnet bundle still needs test keys.
if (
  FLAT_JOINSPLIT_V2_ALLOW_DEVELOPMENT_ARTIFACTS &&
  isProductionChainId(BASE_CHAIN_ID)
) {
  throw new Error(
    "Development JoinSplit V2 proving artifacts are forbidden on production chains",
  );
}

if (
  IS_PRODUCTION_BUILD &&
  BASE_CHAIN_ID === 8453 &&
  FLAT_JOINSPLIT_V2_ENABLED
) {
  for (const name of [
    "REACT_APP_JOINSPLIT_V2_BROWSER_MANIFEST_URL",
    "REACT_APP_JOINSPLIT_V2_BROWSER_MANIFEST_SHA256",
    "REACT_APP_JOINSPLIT_V2_BUNDLED_KEY_BASE_URL",
    "REACT_APP_JOINSPLIT_V2_KEY_CDN_BASE_URL",
    "REACT_APP_JOINSPLIT_V2_DEPLOYMENT_BLOCK",
  ]) {
    if (!String(process.env[name] || "").trim()) {
      throw new Error(`${name} is required for the Base mainnet V2 stack`);
    }
  }
  if (FLAT_JOINSPLIT_V2_DEPLOYMENT_BLOCK <= 0) {
    throw new Error(
      "REACT_APP_JOINSPLIT_V2_DEPLOYMENT_BLOCK must be a positive deployed block for the Base mainnet V2 stack",
    );
  }
}

function requireBaseMainnetEnv(
  name: string,
  fallback: string,
  required = true,
): string {
  const value = process.env[name];
  if (value && value.trim().length > 0) {
    return value;
  }
  if (required && IS_PRODUCTION_BUILD && BASE_CHAIN_ID === 8453) {
    throw new Error(
      `${name} must be configured for Base mainnet production builds`,
    );
  }
  return fallback;
}

export const BASE_CONFIG = {
  API_KEY: "proxy",
  BASE_VERIFICATION_MODE,
  BASE_FUNGIBLE_TOKEN_HASH_MODE,
  BASE_WITHDRAW_BINDING_VERSION,
  PROVING_PROFILE,
  ENABLE_RANGE_SCREENING: ENABLE_WALLET_SCREENING,
  ENABLE_WALLET_SCREENING,
  WALLET_SCREENING_PROVIDER,

  // Fee Configuration
  PROTOCOL_FEE_BPS: BASE_PROTOCOL_FEE_BPS,
  PARTNER_FEE_BPS: BASE_PARTNER_FEE_BPS,
  SWAP_PROTOCOL_FEE_BPS: BASE_SWAP_PROTOCOL_FEE_BPS,
  SWAP_PARTNER_FEE_BPS: BASE_SWAP_PARTNER_FEE_BPS,
  PROTOCOL_FEE_RECIPIENT:
    process.env.REACT_APP_PROTOCOL_FEE_RECIPIENT ||
    "0x28ede7946e2c2d1e5afea1aa0d4c170b134585f6",
  PARTNER_FEE_RECIPIENT:
    process.env.REACT_APP_PARTNER_FEE_RECIPIENT || ethers.ZeroAddress,
  BATCH_WITHDRAW_ENABLED:
    (process.env.REACT_APP_BATCH_WITHDRAW_ENABLED || "true").toLowerCase() ===
    "true",
  NFT_EVM_ENABLED:
    !FLAT_JOINSPLIT_V2_ENABLED &&
    (process.env.REACT_APP_NFT_EVM_ENABLED || "true").toLowerCase() === "true",
};

/**
 * EVM Configuration (the Base deployment)
 */
const BASE_EVM_CONFIG = {
  ...BASE_CONFIG,
  VERIFICATION_MODE: BASE_CONFIG.BASE_VERIFICATION_MODE,
  FUNGIBLE_TOKEN_HASH_MODE: BASE_CONFIG.BASE_FUNGIBLE_TOKEN_HASH_MODE,
  WITHDRAW_BINDING_VERSION: BASE_CONFIG.BASE_WITHDRAW_BINDING_VERSION,
  PROVING_PROFILE,
  SETTLEMENT_STACK: BASE_SETTLEMENT_STACK,
  // Enable only after the active pool's swap adapter and relayer route are
  // deployed and qualified together. The fresh Sepolia pool has no adapter.
  PRIVATE_SWAP_ENABLED:
    !FLAT_JOINSPLIT_V2_ENABLED ||
    process.env.REACT_APP_PRIVATE_SWAP_ENABLED === "true",
  // Notes/history/commitments route through the Base engine proxy (base-engine.stealthos.xyz).
  PROVER_URL: PREVIOUS_POOL_MODE
    ? "/api/proxy/base-engine?previous=1&path="
    : "/api/proxy/base-engine?path=",
  // Legacy data-service aliases; proof generation runs in browser WASM.
  PROVER_DIRECT_URL: "",
  // Retained name for Merkle/relayer discovery callers.
  NOVA_PROVER_URL: PREVIOUS_POOL_MODE
    ? "/api/proxy/base-engine?previous=1&path="
    : "/api/proxy/base-engine?path=",
  BASE_PERPS_CHART_EMBED_URL:
    process.env.REACT_APP_BASE_PERPS_CHART_EMBED_URL || "",
  ARTIFACTS_URL:
    process.env.REACT_APP_BASE_ARTIFACTS_URL ||
    `${DEFAULT_BASE_PROVER_URL}/artifacts`,
  CHAIN_TYPE: "evm" as ChainType,
  CHAIN_ID: BASE_CHAIN_ID, // Base mainnet by default
  // Identity anchors must remain stable when the settlement pool is replaced.
  DERIVATION_POOL: process.env.REACT_APP_BASE_DERIVATION_POOL || "",
  DERIVATION_VERIFIER: process.env.REACT_APP_BASE_DERIVATION_VERIFIER || "",
  BASE_RPC_URL: "/api/proxy/evm-rpc",
  CONTRACTS: {
    SHIELDED_POOL_NOVA: requireBaseMainnetEnv(
      "REACT_APP_SHIELDED_POOL_NOVA",
      "0x2f8FcBa70a873961bf8B53a3fD0D8438528FC603",
    ),
    NOVA_VERIFIER: requireBaseMainnetEnv(
      "REACT_APP_NOVA_VERIFIER",
      "0x349AD82e7b87CE1D122b60dB694e0Ba2A805C1bD",
      BASE_CONFIG.BASE_VERIFICATION_MODE === "zk_required",
    ),
    ATTESTATION_REGISTRY: requireBaseMainnetEnv(
      "REACT_APP_ATTESTATION_REGISTRY",
      "",
      BASE_CONFIG.BASE_VERIFICATION_MODE === "tee_only",
    ),
    NULLIFIER_REGISTRY: requireBaseMainnetEnv(
      "REACT_APP_NULLIFIER_REGISTRY",
      "0xe680C6F74416283e87eA602a5cD0078bCF1a272a",
    ),
    NFT_PRIVACY_VAULT: process.env.REACT_APP_NFT_PRIVACY_VAULT || "",
    SHIELDED_POOL_JOINSPLIT: PREVIOUS_POOL_MODE
      ? PREVIOUS_POOL
      : requireBaseMainnetEnv(
          "REACT_APP_SHIELDED_POOL_JOINSPLIT",
          "",
          FLAT_JOINSPLIT_V2_ENABLED,
        ),
  },
  JOINSPLIT_V2: {
    ENABLED: FLAT_JOINSPLIT_V2_ENABLED,
    DEPLOYMENT_BLOCK: PREVIOUS_POOL_MODE
      ? 47137775
      : FLAT_JOINSPLIT_V2_DEPLOYMENT_BLOCK,
    FINALITY_BLOCKS: positiveIntegerEnv(
      "REACT_APP_JOINSPLIT_V2_FINALITY_BLOCKS",
      12,
    ),
    LOG_CHUNK_SIZE: positiveIntegerEnv(
      "REACT_APP_JOINSPLIT_V2_LOG_CHUNK_SIZE",
      20_000,
    ),
    /**
     * Route withdrawals through the relayer. Paying gas from the user's own
     * wallet links it to the withdrawal, so this is off only for local testing.
     */
    RELAY_WITHDRAWALS:
      String(process.env.REACT_APP_JOINSPLIT_V2_RELAY_WITHDRAWALS || "true")
        .trim()
        .toLowerCase() === "true",
    /**
     * Relayer fee in basis points of the withdrawal amount. Paid to the pool's
     * `feeRecipient`, which the relayer requires to be an address it controls.
     */
    RELAY_FEE_BPS: positiveIntegerEnv(
      "REACT_APP_JOINSPLIT_V2_RELAY_FEE_BPS",
      30,
    ),
    BROWSER_MANIFEST_URL:
      process.env.REACT_APP_JOINSPLIT_V2_BROWSER_MANIFEST_URL || "",
    BROWSER_MANIFEST_SHA256: exactSha256Env(
      "REACT_APP_JOINSPLIT_V2_BROWSER_MANIFEST_SHA256",
    ),
    BUNDLED_KEY_BASE_URL:
      process.env.REACT_APP_JOINSPLIT_V2_BUNDLED_KEY_BASE_URL || "",
    KEY_CDN_BASE_URL: process.env.REACT_APP_JOINSPLIT_V2_KEY_CDN_BASE_URL || "",
    ALLOW_DEVELOPMENT_ARTIFACTS: FLAT_JOINSPLIT_V2_ALLOW_DEVELOPMENT_ARTIFACTS,
    PROVE_TIMEOUT_MS: positiveIntegerEnv(
      "REACT_APP_JOINSPLIT_V2_PROVE_TIMEOUT_MS",
      120_000,
    ),
    KEY_LOAD_TIMEOUT_MS: positiveIntegerEnv(
      "REACT_APP_JOINSPLIT_V2_KEY_LOAD_TIMEOUT_MS",
      180_000,
    ),
    /** Floor for a relayed fee in wei of the native coin; Base has none. */
    RELAY_MIN_FEE_WEI: 0n as bigint,
    /** Live pre-proof relay fee quotes (DogeOS Chikyu); Base keeps its fees. */
    RELAY_FEE_QUOTES: false as boolean,
    /** Incremental balance refresh (verified sync head, deltas, pinned tree
     * checkpoints): rolled out on Base Sepolia, not yet on Base mainnet. */
    INCREMENTAL_SYNC: (BASE_CHAIN_ID === 84532) as boolean,
    /** Largest circuit arity a settlement may use; the full ladder on Base. */
    MAX_ARITY_ID: 4,
  },
  NFT_WRAPPED_MODE_DEFAULT:
    (process.env.REACT_APP_NFT_WRAPPED_MODE_DEFAULT || "true").toLowerCase() ===
    "true",
  NETWORK: baseNetworkDescriptor(),
};

/**
 * The EVM_CONFIG shape DogeOS Chikyu serves: the shared prover artifacts,
 * timeouts and fee policy with the network's own chain, pool, router,
 * proxies and token. No Base address survives into this record.
 */
function dogeOsEvmConfig(network: DogeOsChikyuConfig): typeof BASE_EVM_CONFIG {
  return {
    ...BASE_EVM_CONFIG,
    SETTLEMENT_STACK: "flat_joinsplit_v2",
    PRIVATE_SWAP_ENABLED:
      network.enabled &&
      process.env.REACT_APP_DOGEOS_CHIKYU_MUCHFI_SWAP_ENABLED === "true",
    NFT_EVM_ENABLED: false,
    ENABLE_RANGE_SCREENING: false,
    ENABLE_WALLET_SCREENING: false,
    BASE_PERPS_CHART_EMBED_URL: "",
    PROVER_URL: network.engineProxyUrl,
    NOVA_PROVER_URL: network.engineProxyUrl,
    CHAIN_ID: network.chainId,
    DERIVATION_POOL: network.derivationPool,
    DERIVATION_VERIFIER: network.derivationVerifier,
    BASE_RPC_URL: network.rpcProxyUrl,
    CONTRACTS: {
      ...BASE_EVM_CONFIG.CONTRACTS,
      SHIELDED_POOL_NOVA: "",
      NOVA_VERIFIER: "",
      ATTESTATION_REGISTRY: "",
      NULLIFIER_REGISTRY: "",
      NFT_PRIVACY_VAULT: "",
      SHIELDED_POOL_JOINSPLIT: network.shieldedPoolJoinSplit,
    },
    JOINSPLIT_V2: {
      ...BASE_EVM_CONFIG.JOINSPLIT_V2,
      ENABLED: true,
      DEPLOYMENT_BLOCK: network.deploymentBlock,
      FINALITY_BLOCKS: network.finalityBlocks,
      LOG_CHUNK_SIZE: network.logChunkSize,
      RELAY_WITHDRAWALS: network.relayWithdrawals,
      RELAY_FEE_BPS: network.relayFeeBps,
      RELAY_MIN_FEE_WEI: network.relayMinFeeWei,
      RELAY_FEE_QUOTES: network.relayFeeQuotes,
      INCREMENTAL_SYNC: network.incrementalSync,
      ALLOW_DEVELOPMENT_ARTIFACTS: network.allowDevelopmentArtifacts,
      MAX_ARITY_ID: network.maxArityId,
    },
    NETWORK: dogeOsNetworkDescriptor(network),
  };
}

/** The configuration of the network this page serves (see ACTIVE_EVM_NETWORK_KEY). */
export const EVM_CONFIG = DOGEOS_ACTIVE
  ? dogeOsEvmConfig(DOGEOS_CHIKYU)
  : BASE_EVM_CONFIG;

/** The pool that owns the active Base private-note namespace. */
export function getActiveBaseShieldedPoolAddress(): string {
  return EVM_CONFIG.JOINSPLIT_V2.ENABLED
    ? EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT
    : EVM_CONFIG.CONTRACTS.SHIELDED_POOL_NOVA;
}

export interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
  isNative: boolean;
  tokenType?: "erc20" | "erc721" | "erc1155" | "vnft";
  tokenSubID?: string;
  isWrappedNft?: boolean;
  wrappedClaimHash?: string;
  wrappedVaultIdx?: string;
  wrappedNonce?: string;
  wrappedUnderlyingTokenAddress?: string;
  wrappedUnderlyingTokenType?: "erc721" | "erc1155";
  wrappedUnderlyingTokenSubID?: string;
  wrappedName?: string;
}

export const BASE_MAINNET_TOKENS: TokenInfo[] = [
  {
    address: ethers.ZeroAddress,
    symbol: "ETH",
    name: "Ether",
    decimals: 18,
    isNative: true,
  },
  {
    address: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    isNative: false,
  },
];

const BASE_TESTNET_TOKENS: TokenInfo[] = [
  {
    address: ethers.ZeroAddress,
    symbol: "ETH",
    name: "Ether",
    decimals: 18,
    isNative: true,
  },
  {
    address: "0x036CbD53842c5426634e7929541eC2318f3dCF7e",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    isNative: false,
  },
];

/** Keep received assets visible even if the swap UI is disabled for maintenance. */
export const DOGEOS_CHIKYU_TOKENS: TokenInfo[] = [
  {
    address: ethers.ZeroAddress,
    symbol: "DOGE",
    name: "Dogecoin",
    decimals: 18,
    isNative: true,
  },
  {
    address: MUCHFI_CHIKYU.usdc,
    symbol: "USDC",
    name: "USD Coin (testnet)",
    decimals: MUCHFI_CHIKYU.usdcDecimals,
    isNative: false,
  },
];

/** The token list of the network this page serves (historical name kept). */
export const BASE_SEPOLIA_TOKENS: TokenInfo[] = DOGEOS_ACTIVE
  ? DOGEOS_CHIKYU_TOKENS
  : EVM_CONFIG.CHAIN_ID === 8453
    ? BASE_MAINNET_TOKENS
    : BASE_TESTNET_TOKENS;

export function getTokenByAddress(address: string): TokenInfo | undefined {
  return BASE_SEPOLIA_TOKENS.find(
    (t) => t.address.toLowerCase() === address.toLowerCase(),
  );
}

export function getTokenBySymbol(symbol: string): TokenInfo | undefined {
  return BASE_SEPOLIA_TOKENS.find(
    (t) => t.symbol.toUpperCase() === symbol.toUpperCase(),
  );
}

// Cache buster to force fresh artifact downloads after circuit updates
export const ARTIFACT_VERSION = "v13-mpk-binding-20260220";

/**
 * Get config for a specific chain type. Only EVM remains.
 */
export function getConfig(_chainType?: ChainType) {
  return EVM_CONFIG;
}

/**
 * Default chain type (can be overridden by env)
 */
export const DEFAULT_CHAIN_TYPE: ChainType =
  (process.env.REACT_APP_DEFAULT_CHAIN as ChainType) || "evm";

export default {
  BASE_CONFIG,
  EVM_CONFIG,
  ARTIFACT_VERSION,
  getConfig,
  DEFAULT_CHAIN_TYPE,
};
